import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_ASSISTANT, INPUT_SPECIALISTS, Specialist } from '../Utils/specialists';
import {
  AIChatMessage,
  MessageRole,
  useGetMessages,
  useSendMessageToThread,
  useSendNewThreadChat
} from './hooks';
import { ChatMessage } from './types';

let localIdCounter = 0;
const createLocalId = () => {
  localIdCounter += 1;
  return `local_${localIdCounter}`;
};

/** A API devolve as mensagens da mais recente para a mais antiga. */
const fromServer = (messages: AIChatMessage[], threadId: string): ChatMessage[] =>
  [...messages].reverse().map((message) => ({
    localId: message.messageId,
    messageId: message.messageId,
    role: message.role,
    content: message.content,
    threadId
  }));

/** Mantém o efeito de digitação de uma resposta local que acabou de ser sincronizada. */
const mergeWithLocal = (server: ChatMessage[], local: ChatMessage[]) =>
  server.map((message, index) => {
    const localMessage = local[index];
    return localMessage?.animate && localMessage.content === message.content
      ? { ...message, animate: true }
      : message;
  });

export interface UseChatParams {
  constructionSiteId: number;
  selectedThreadId?: string;
  setSelectedThreadId: (threadId: string | undefined) => void;
  assistant?: string;
}

const useChat = ({
  constructionSiteId,
  selectedThreadId,
  setSelectedThreadId,
  assistant
}: UseChatParams) => {
  const { t } = useTranslation('activities');
  const [value, setValue] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [chatAssistant, setChatAssistant] = useState<string | undefined>(assistant);
  const [assistantChosen, setAssistantChosen] = useState(Boolean(assistant));
  const messagesRef = useRef<ChatMessage[]>([]);
  const createdThreadIdRef = useRef<string>();
  const lastSendAtRef = useRef(0);

  messagesRef.current = messages;

  // Sem escolha explícita de especialista, a conversa usa o assistente unificado
  const effectiveAssistant = chatAssistant ?? assistant ?? DEFAULT_ASSISTANT;
  const { mutateAsync: createThread } = useSendNewThreadChat(
    constructionSiteId,
    effectiveAssistant
  );
  const { mutateAsync: streamToThread } = useSendMessageToThread(
    constructionSiteId,
    effectiveAssistant
  );
  const { data: threadData, dataUpdatedAt, isLoading: isLoadingThread } = useGetMessages(
    constructionSiteId,
    selectedThreadId
  );

  // Troca de thread (ou "Novo chat"): limpa a conversa local, exceto quando a thread
  // acabou de ser criada por este próprio chat.
  // Ao trocar de thread o especialista volta ao padrão (unificado). No mount não reseta,
  // para manter o especialista escolhido na sidebar ou na biblioteca.
  const isFirstThreadEffectRef = useRef(true);
  useEffect(() => {
    if (selectedThreadId && selectedThreadId === createdThreadIdRef.current) return;
    createdThreadIdRef.current = undefined;
    setMessages([]);
    setValue('');
    if (isFirstThreadEffectRef.current) {
      isFirstThreadEffectRef.current = false;
      return;
    }
    setChatAssistant(undefined);
    setAssistantChosen(false);
  }, [selectedThreadId]);

  useEffect(() => {
    setChatAssistant(assistant);
    setAssistantChosen(Boolean(assistant));
  }, [assistant]);

  // Sincroniza com o servidor apenas dados posteriores ao último envio, para que o cache
  // antigo não apague a mensagem recém-enviada antes do refetch chegar.
  useEffect(() => {
    if (!threadData || isSending || threadData.threadId !== selectedThreadId) return;
    if (dataUpdatedAt < lastSendAtRef.current) return;
    setMessages(
      mergeWithLocal(
        fromServer(threadData.messages, threadData.threadId),
        messagesRef.current
      )
    );
  }, [threadData, dataUpdatedAt, isSending, selectedThreadId]);

  const updateMessage = useCallback((localId: string, patch: Partial<ChatMessage>) => {
    setMessages((current) =>
      current.map((message) =>
        message.localId === localId ? { ...message, ...patch } : message
      )
    );
  }, []);

  const send = useCallback(
    async (text: string) => {
      const content = text.trim();
      if (!content || isSending) return;

      const answerId = createLocalId();
      setValue('');
      setIsSending(true);
      setMessages((current) => [
        ...current,
        {
          localId: createLocalId(),
          role: MessageRole.USER,
          content,
          threadId: selectedThreadId
        },
        {
          localId: answerId,
          role: MessageRole.BOT,
          content: '',
          threadId: selectedThreadId,
          pending: true
        }
      ]);

      try {
        if (!selectedThreadId) {
          const { threadId, messageId, response } = await createThread(content);
          createdThreadIdRef.current = threadId;
          updateMessage(answerId, {
            content: response,
            messageId,
            threadId,
            pending: false,
            animate: true
          });
          setSelectedThreadId(threadId);
        } else {
          await streamToThread({
            threadId: selectedThreadId,
            message: content,
            onChunk: (chunk) =>
              setMessages((current) =>
                current.map((message) =>
                  message.localId === answerId
                    ? { ...message, content: message.content + chunk }
                    : message
                )
              )
          });
          updateMessage(answerId, { pending: false });
        }
      } catch {
        const partial = messagesRef.current.find(
          (message) => message.localId === answerId
        );
        updateMessage(
          answerId,
          partial?.content
            ? { pending: false }
            : { content: t('aiChat.loadError'), pending: false, isError: true }
        );
      } finally {
        lastSendAtRef.current = Date.now();
        setIsSending(false);
      }
    },
    [
      createThread,
      isSending,
      selectedThreadId,
      setSelectedThreadId,
      streamToThread,
      t,
      updateMessage
    ]
  );

  const selectedSpecialist = useMemo<Specialist | undefined>(
    () =>
      assistantChosen
        ? INPUT_SPECIALISTS.find((option) => option.assistant === chatAssistant)
        : undefined,
    [assistantChosen, chatAssistant]
  );

  const selectSpecialist = useCallback((specialist: Specialist) => {
    setChatAssistant(specialist.assistant);
    setAssistantChosen(true);
  }, []);

  return {
    value,
    setValue,
    messages,
    isSending,
    isLoadingThread: Boolean(selectedThreadId) && isLoadingThread,
    hasConversation: Boolean(selectedThreadId) || messages.length > 0,
    send,
    submit: () => send(value),
    selectedSpecialist,
    selectSpecialist
  };
};

export default useChat;
